import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../rbac/AuthContext";

import { db } from "../../firebase";
import {
  doc, onSnapshot, getDoc, updateDoc, arrayUnion, serverTimestamp,
  query, where, writeBatch, arrayRemove, collection, getDocs
} from "firebase/firestore";
import {
  Container, Card, Spinner, Alert, Nav, Button, Table, Form, Modal
} from "react-bootstrap";

import AddBlock from "../Manager/AddBlock";
import MembersPanel from "../Manager/MembersPanel";
import CreateEvent from "../events/CreateEvent";
import Visitors from "../Visitors/Visitors";
import ResidentApprovals from "../Resident/ResidentApprovals";

// Adjust import paths if your folder structure differs
import SecurityPanel from "../../Security/SecurityPanel";
import StaffTab from "../../Security/StaffTab";

const formatTs = (ts) => {
  try {
    return ts?.toDate?.()?.toLocaleString?.() || "—";
  } catch {
    return "—";
  }
};

const makeId = (prefix = "ID") =>
  `${prefix}-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

export default function CommunityDashboard() {
  const { id: adminCommunityId, communityId: managerResidentCommunityId } = useParams();
  const communityParamId = adminCommunityId || managerResidentCommunityId;

  const { currentUser, role, isAdmin, isManager, isResident } = useAuth();
  const navigate = useNavigate();

  const [community, setCommunity] = useState(null);
  const [unitsMap, setUnitsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // 🔹 Selected section (tab)
  // Allowed values: dashboard | events | staff | security | members | blocks | visitors
  const [section, setSection] = useState("dashboard");

  // Forms/state
  const [newMemberUid, setNewMemberUid] = useState("");
  const [eventForm, setEventForm] = useState({ title: "", date: "", description: "" });
  const [memberSearch, setMemberSearch] = useState("");

  // Events state (top-level events collection)
  const [eventsDocs, setEventsDocs] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Role change modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleModalLoading, setRoleModalLoading] = useState(false);
  const [roleModalError, setRoleModalError] = useState("");
  const [targetUser, setTargetUser] = useState(null); // { uid, displayName, email, role }
  const [newRole, setNewRole] = useState("resident");

  const isManagerOrAdmin = isAdmin || isManager;
  const isSecurityOnly = !isAdmin && !isManager && role === "security";
  const showStaff = isManagerOrAdmin;                        // Staff tab only for managers/admins
  const showSecurity = isSecurityOnly || isManagerOrAdmin;   // Allow manager to view security panel too (optional)

  // Default landing section for strictly security users
  useEffect(() => {
    if (isSecurityOnly && section === "dashboard") {
      setSection("security");
    }
  }, [isSecurityOnly, section]);

  // Load events by communityId
  useEffect(() => {
    if (!community?.id) {
      setEventsDocs([]);
      setEventsLoading(false);
      return;
    }
    setEventsLoading(true);
    setEventsError("");

    const qEv = query(collection(db, "events"), where("communityId", "==", community.id));
    const unsub = onSnapshot(
      qEv,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEventsDocs(rows);
        setEventsLoading(false);
      },
      (err) => {
        console.error("Events snapshot error:", err);
        setEventsError(err?.message || "Failed to load events.");
        setEventsLoading(false);
      }
    );
    return () => unsub();
  }, [community?.id]);

  // Real-time subscription to community doc
  useEffect(() => {
    if (!communityParamId) return;
    setLoading(true);
    setLoadError("");

    const ref = doc(db, "communities", communityParamId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setLoadError("Community not found.");
          setCommunity(null);
          setLoading(false);
          return;
        }
        setCommunity({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      (err) => {
        console.error("onSnapshot error:", err.code, err.message);
        setLoadError(err?.message || "Failed to load community.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [communityParamId]);

useEffect(() => {
  if (!Array.isArray(community?.blocks)) {
    setUnitsMap({});
    return;
  }

  const data = {};

  community.blocks.forEach((savedBlock) => {
    const flatDetails =
      savedBlock?.flatDetails &&
      typeof savedBlock.flatDetails === "object"
        ? savedBlock.flatDetails
        : {};

    Object.entries(flatDetails).forEach(([flatNumber, flatDetail]) => {
      data[flatNumber] = {
        ...flatDetail,
        blockId: savedBlock.id || "",
        blockName: savedBlock.name || "",
        flatNumber,
      };
    });
  });

  setUnitsMap(data);
  console.log("Flat details loaded from community blocks:", data);
}, [community?.blocks]);

  const membersCount = useMemo(
    () => (Array.isArray(community?.members) ? community.members.length : 0),
    [community]
  );

  /** 🔒 Role-based section access guard */
  const canAccess = (key) => {
    if (key === "dashboard" || key === "events" || key === "visitors") return true;
    if (key === "members" || key === "blocks" || key === "staff") return isManagerOrAdmin;
    if (key === "security") return showSecurity;
    return false;
  };

  const onSelectSection = (key) => {
    if (!key) return;
    if (canAccess(key)) {
      setSection(key);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /** Admin/Manager-only: add/remove members */
  const addMember = async () => {
    if (!isAdmin && !isManager) return alert("Not allowed.");
    const uid = newMemberUid.trim();
    if (!uid) return alert("Enter a user UID to add.");
    try {
      const ref = doc(db, "communities", communityParamId);
      await updateDoc(ref, { members: arrayUnion(uid) });
      setNewMemberUid("");
    } catch (err) {
      console.error("addMember error:", err);
      alert(err?.message || "Failed to add member.");
    }
  };

  // Open modal for a specific user UID
  const openRoleModalFor = async (uid) => {
    if (!uid) return;
    if (!isAdmin && !isManager) {
      alert("Not allowed.");
      return;
    }
    try {
      setRoleModalError("");
      setRoleModalLoading(true);

      const ref = doc(db, "users", uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setRoleModalError("User not found.");
        setRoleModalLoading(false);
        setShowRoleModal(true);
        return;
      }
      const data = snap.data();
      const currentRole = (data?.role || "resident").toLowerCase();

      // Manager cannot edit Admin users
      if (isManager && currentRole === "admin") {
        setRoleModalError("Managers cannot modify admin users.");
      }

      setTargetUser({
        uid,
        displayName: data?.fullName || data?.displayName || data?.email || uid,
        email: data?.email || "",
        role: currentRole,
      });

      setNewRole(currentRole);
      setShowRoleModal(true);
    } catch (err) {
      console.error("Failed to load user for role edit:", err);
      setRoleModalError(err?.message || "Failed to load user.");
      setShowRoleModal(true);
    } finally {
      setRoleModalLoading(false);
    }
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setRoleModalError("");
    setTargetUser(null);
    setNewRole("resident");
  };

  // Persist the role change to Firestore
  const saveRoleChange = async () => {
    if (!targetUser?.uid) return;
    if (!isAdmin && !isManager) {
      alert("Not allowed.");
      return;
    }

    const allowedRolesForManager = ["resident", "manager"];
    if (isManager) {
      if (targetUser.role === "admin") {
        setRoleModalError("Managers cannot modify admin users.");
        return;
      }
      if (!allowedRolesForManager.includes(newRole)) {
        setRoleModalError("Managers can only set 'resident' or 'manager'.");
        return;
      }
    }

    if (newRole === targetUser.role) {
      closeRoleModal();
      return;
    }

    try {
      setRoleModalLoading(true);
      await updateDoc(doc(db, "users", targetUser.uid), {
        role: newRole,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid || null,
      });
      closeRoleModal();
    } catch (err) {
      console.error("Failed to update role:", err);
      setRoleModalError(err?.message || "Failed to update role.");
    } finally {
      setRoleModalLoading(false);
    }
  };

  const removeMember = async (uid) => {
    if (!isAdmin && !isManager) return alert("Not allowed.");
    if (!window.confirm("Remove this member UID from the community?")) return;
    try {
      const ref = doc(db, "communities", communityParamId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const data = snap.data();
      const next = (data.members || []).filter((m) => m !== uid);
      await updateDoc(ref, { members: next });
    } catch (err) {
      console.error("removeMember error:", err);
      alert(err?.message || "Failed to remove member.");
    }
  };

  const openEditEventModal = (eventData) => {
    setSelectedEvent(eventData);
    setShowEditEventModal(true);
  };

  const closeEditEventModal = () => {
    setSelectedEvent(null);
    setShowEditEventModal(false);
  };


  /** Admin/Manager-only: delete event + remove references */
  const deleteEvent = async (eventId) => {
    if (!isAdmin && !isManager) return alert("Not allowed.");
    if (!window.confirm("Delete this event?")) return;
    try {
      const communityRef = doc(db, "communities", community.id);
      const eventRef = doc(db, "events", eventId);
      const batch = writeBatch(db);
      batch.delete(eventRef);
      batch.update(communityRef, { eventIds: arrayRemove(eventId) });
      batch.update(communityRef, { events: arrayRemove(eventId) });
      await batch.commit();
    } catch (err) {
      console.error("Delete event error:", err);
      alert(err?.message || "Failed to delete event.");
    }
  };

  /** Admin/Manager-only: add inline event under community.events */
  const addEvent = async () => {
    if (!isAdmin && !isManager) return alert("Not allowed.");
    const { title, date, description } = eventForm;
    if (!title || !date) {
      alert("Title and date are required.");
      return;
    }
    const entry = {
      id: makeId("EVENT"),
      title,
      date, // YYYY-MM-DD
      description: description || "",
      createdAt: serverTimestamp(),
    };
    try {
      const ref = doc(db, "communities", communityParamId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const data = snap.data();
      const next = Array.isArray(data.events) ? [...data.events, entry] : [entry];
      await updateDoc(ref, { events: next });
      setEventForm({ title: "", date: "", description: "" });
    } catch (err) {
      console.error("addEvent error:", err);
      alert(err?.message || "Failed to add event.");
    }
  };

  // --- Mobile-only sticky, scrollable tab bar ---
  const MobileTabs = (
    <div className="sticky-mobile-tabs bg-white border-bottom d-md-none">
      <div className="px-2 py-2 mobile-nav-scroll">
        <Nav
          variant="pills"
          className="flex-nowrap"
          activeKey={section}
          onSelect={onSelectSection}
        >
          <Nav.Item><Nav.Link eventKey="dashboard">Dashboard</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="events">Events</Nav.Link></Nav.Item>

          {showStaff && (
            <Nav.Item><Nav.Link eventKey="staff">Staff</Nav.Link></Nav.Item>
          )}
          {showSecurity && (
            <Nav.Item><Nav.Link eventKey="security">Security</Nav.Link></Nav.Item>
          )}

          {isManagerOrAdmin && (
            <Nav.Item><Nav.Link eventKey="members">Members</Nav.Link></Nav.Item>
          )}
          {isManagerOrAdmin && (
            <Nav.Item><Nav.Link eventKey="blocks">Blocks &amp; Flats</Nav.Link></Nav.Item>
          )}

          <Nav.Item><Nav.Link eventKey="visitors">Visitors</Nav.Link></Nav.Item>
        </Nav>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading community…</span>
        </div>
      </Container>
    );
  }

  if (loadError || !community) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{loadError || "Community not found."}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-3 content-safe-bottom">
      {/* Scoped styles for the mobile tabs */}
      <style>{`
        .mobile-nav-scroll {
          overflow-x: auto;
          white-space: nowrap;
          -webkit-overflow-scrolling: touch;
        }
        .sticky-mobile-tabs {
          position: sticky;
          top: var(--app-header-height, 56px);
          z-index: 1020; /* Above cards/tables but below modals */
        }
        .mobile-nav-scroll .nav-link {
          padding: .375rem .6rem;
          font-weight: 600;
          font-size: .875rem;
        }
        .content-safe-bottom {
          padding-bottom: var(--app-bottom-nav-height, 72px);
        }
      `}</style>

      {/* Mobile horizontal tabs */}
      {MobileTabs}

      <div className="row mt-2 mt-md-0">
        <Card className="mb-3">
          <Card.Body>
            <div>
              <strong>Community Name:</strong>{" "}
              <strong className="text-primary-label">{community.name}</strong>{" "}
              - (<code>{community.id}</code>)
            </div>
            <div><strong>Address:</strong> {community.address || "—"}</div>
          </Card.Body>
        </Card>

        {/* LEFT: Sidebar (desktop only) */}
        <div className="col-12 col-md-3 col-lg-2 mb-3 d-none d-md-block">
          <Card>
            <Card.Header><strong>{community.name || "Community"}</strong></Card.Header>
            <Nav
              className="flex-column"
              variant="pills"
              activeKey={section}
              onSelect={onSelectSection}
            >
              <Nav.Link eventKey="dashboard">📊 Dashboard</Nav.Link>
              <Nav.Link eventKey="events">📅 Events</Nav.Link>

              {showStaff && <Nav.Link eventKey="staff">🧑‍🔧 Staff</Nav.Link>}
              {showSecurity && <Nav.Link eventKey="security">🛡️ Security</Nav.Link>}

              {isManagerOrAdmin && <Nav.Link eventKey="members">👥 Members</Nav.Link>}
              {isManagerOrAdmin && <Nav.Link eventKey="blocks">🏢 Blocks &amp; Flats</Nav.Link>}

              <Nav.Link eventKey="visitors">🛂 Visitors</Nav.Link>
            </Nav>
          </Card>
        </div>

        {/* RIGHT: Main content */}
        <div className="col-12 col-md-9 col-lg-10">
          {section === "dashboard" && (
            <Card>
              <Card.Header>Dashboard</Card.Header>
              <Card.Body>
                <div className="row g-3">
                  <div className="col-md-3">
                    <Card bg="light"><Card.Body>
                      <h6 className="text-muted">Members</h6>
                      <h3>{membersCount}</h3>
                    </Card.Body></Card>
                  </div>
                  <div className="col-md-3">
                    <Card bg="light"><Card.Body>
                      <h6 className="text-muted">Association</h6>
                      <h3>{community.association?.length || 0}</h3>
                    </Card.Body></Card>
                  </div>
                  <div className="col-md-3">
                    <Card bg="light"><Card.Body>
                      <h6 className="text-muted">Events</h6>
                      <h3>{community.events?.length || 0}</h3>
                    </Card.Body></Card>
                  </div>
                  <div className="col-md-3">
                    <Card bg="light"><Card.Body>
                      <h6 className="text-muted">Blocks</h6>
                      <h3>{community.blocks?.length || 0}</h3>
                    </Card.Body></Card>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {section === "events" && (
            <Card>
              <Card.Header>Events</Card.Header>
              <Card.Body>
                {isManagerOrAdmin && <CreateEvent communityId={community.id} />}

                {eventsError && <Alert variant="danger" className="mt-3">{eventsError}</Alert>}

                {eventsLoading ? (
                  <div className="d-flex align-items-center mt-2">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading events…</span>
                  </div>
                ) : (
                  <Table bordered hover responsive size="sm" className="mt-3">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Committee</th>
                        <th style={{ width: 260 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventsDocs.length === 0 ? (
                        <tr><td colSpan={5} className="text-center">No events</td></tr>
                      ) : (
                        eventsDocs.map((ev) => (
                          <tr key={ev.id}>
                            <td>{ev.name || ev.title || "—"}</td>
                            <td>{ev.date || "—"}</td>
                            <td className="text-truncate" style={{ maxWidth: 360 }}>
                              {ev.description || "—"}
                            </td>
                            <td>
                              {Array.isArray(ev.committee)
                                ? ev.committee.length
                                : (ev.committee && typeof ev.committee === "object")
                                  ? Object.keys(ev.committee).length
                                  : 0}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                {/* <td className="d-flex gap-2"> */}
                                <Button
                                  variant="outline-primary"
                                  size="md"
                                  onClick={() => navigate(`/community/${community.id}/events/${ev.id}`)}
                                >
                                  View
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="md"
                                  onClick={() => openEditEventModal(ev)}
                                >
                                  Edit
                                </Button>
                                {isManagerOrAdmin && (
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => deleteEvent(ev.id)}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}

          {/* ✅ Staff renders on the RIGHT only */}
          {section === "staff" && showStaff && (
            <StaffTab community={community} />
          )}

          {/* ✅ Security renders on the RIGHT only */}
          {section === "security" && showSecurity && (
            <SecurityPanel community={community} />
          )}

          {section === "members" && isManagerOrAdmin && (
            <Card>
              <Card.Header className="d-none d-lg-block">Members</Card.Header>
              <Card.Body>
                <Form.Control
                  type="search"
                  placeholder="Search members by name, email, or UID…"
                  aria-label="Search members"
                  className="mb-3"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />

                <MembersPanel
                  communityId={community.id}
                  canEditRole={true}
                  onEditRole={(uid) => openRoleModalFor(uid)}
                  searchQuery={memberSearch}
                />

                <hr />
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Enter user UID"
                    value={newMemberUid}
                    onChange={(e) => setNewMemberUid(e.target.value)}
                  />
                  <Button variant="primary" onClick={addMember}>Add</Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {section === "blocks" && isManagerOrAdmin && (
            <Card>
              <Card.Header className="d-none d-lg-block">Blocks &amp; Flats</Card.Header>
              <Card.Body>
                <AddBlock communityId={community.id} />
                {Array.isArray(community.blocks) && community.blocks.length > 0 && (
                  <>
                    <hr />
                    <h5>Saved Blocks</h5>
                    {community.blocks.map((b) => (
                      <div key={b.id} className="mb-4">
                        <strong>Block {b.name}</strong> • Floors: {b.floors} • Flats/Floor: {b.flatsPerFloor}
                        <div className="text-muted">Range: {b.startNumber} → {b.endNumber}</div>

                        <Table bordered responsive size="sm" className="mt-2">
                          <thead>
                            <tr>
                              <th>Floor</th>
                              <th>Flats</th>
                            </tr>
                          </thead>
                          <tbody>
                            {b.hasGround && b.groundFlatsCount > 0 && (
                              <tr>
                                <td>Ground ({b.groundLabel})</td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2">
                                    {Array.from({ length: b.groundFlatsCount }).map((_, i) => {
                                      const idx = (b.groundIndexStart ?? 1) + i;
                                      const width = b.groundIndexWidth ?? 2;
                                      const label = `${b.name}-${b.groundLabel}${String(idx).padStart(width, "0")}`;
                                      return <code key={label}>{label}</code>;
                                    })}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {Array.from({ length: b.floors }).map((_, fi) => {
                              const f = fi + 1;
                              return (
                                <tr key={`floor-${f}`}>
                                  <td>Floor {f}</td>
                                  <td>
                                    <div className="d-flex flex-wrap gap-2">
                                      {Array.from({ length: b.flatsPerFloor }).map((__, i) => {
                                        const idx = (b.upperIndexStart ?? 1) + i;
                                        const width = b.upperIndexWidth ?? 2;
                                        // const label = `${b.name}-${f}${String(idx).padStart(width, "0")}`;
                                        // return <code key={label}>{label}</code>;
                                        const label = `${b.name}-${f}${String(idx).padStart(width, "0")}`;
                                        const unit = unitsMap[label];

                                        return (
                                          // <div
                                          //   key={label}
                                          //   className="border rounded p-2 mb-2 bg-light"
                                          //   style={{
                                          //     minWidth: "220px"
                                          //   }}
                                          // >
                                          //   <div>
                                          //     <strong>{label}</strong>
                                          //   </div>

                                          //   <div>
                                          //     BHK :, 
                                          //     <strong>
                                          //       {" "}
                                          //       {unit?.roomType || "-"}
                                          //     </strong>  {"  "}

                                          //     Sqft : 
                                          //     <strong>
                                          //       {" "}
                                          //       {unit?.squareFeet || "-"}
                                          //     </strong>
                                          //   </div>

                                          //   <div>
                                          //     Maintenance :
                                          //     <strong>
                                          //       {" "}
                                          //       ₹
                                          //       {unit?.maintenanceAmount || 0}
                                          //     </strong>
                                          //   </div>
                                          // </div>

                                          <div
                                            key={label}
                                            className="border rounded shadow-sm p-3 bg-white"
                                            style={{ minWidth: "200px" }}
                                          >
                                            <h6 className="mb-2 text-primary">{label}</h6>

                                            <div className="mb-1">
                                              {/* <strong>BHK:</strong>  */}
                                              <strong>{unit?.roomType || "-"}</strong>
                                            {/* </div>

                                            <div className="mb-1"> */}
                                              {/* <strong>Area:</strong>  */} {" "}
                                              ({unit?.squareFeet || "-"} Sq Ft) 
                                            </div>

                                            <div>
                                              <strong>Maintenance:</strong>
                                              <span className="text-success fw-bold">
                                                {" "}₹{unit?.maintenanceAmount || 0}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    ))}
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {section === "visitors" && (
            <Card>
              <Card.Header>Visitors</Card.Header>
              <Card.Body>
                <Visitors
                  community={community}
                  currentUser={currentUser}
                  isAdmin={isAdmin}
                  isManager={isManager}
                  isResident={isResident}
                />
                {isResident && (
                  <>
                    <hr />
                    <ResidentApprovals communityId={community.id} />
                  </>
                )}
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      {/* Role change modal */}
      <Modal show={showRoleModal} onHide={closeRoleModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {roleModalLoading ? (
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Loading…</span>
            </div>
          ) : (
            <>
              {roleModalError && <Alert variant="warning">{roleModalError}</Alert>}

              <div className="mb-2">
                <div><strong>User:</strong> {targetUser?.displayName || "—"}</div>
                <div className="text-muted"><small>{targetUser?.email || ""}</small></div>
              </div>

              <Form.Group className="mt-2">
                <Form.Label>New Role</Form.Label>
                <Form.Select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  disabled={isManager && targetUser?.role === "admin"}
                >
                  {isAdmin && (
                    <>
                      <option value="admin">admin</option>
                      <option value="manager">manager</option>
                      <option value="resident">resident</option>
                    </>
                  )}
                  {isManager && (
                    <>
                      <option value="manager">manager</option>
                      <option value="resident">resident</option>
                    </>
                  )}
                </Form.Select>
                <div className="form-text">
                  {isManager ? "Managers can update only resident/manager roles." : "Admins can set any role."}
                </div>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeRoleModal} disabled={roleModalLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveRoleChange}
            disabled={roleModalLoading || (isManager && targetUser?.role === "admin")}
          >
            {roleModalLoading ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showEditEventModal}
        onHide={closeEditEventModal}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Event</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedEvent && (
            <CreateEvent
              communityId={community.id}
              editEvent={selectedEvent}
              mode="edit"
              onSuccess={closeEditEventModal}
            />
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}